import React, { useEffect, useState } from 'react';
import { collection, query, getDocs, doc, updateDoc, addDoc, serverTimestamp, deleteDoc, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { runMigration } from '../migration';

function Admin({ user, userData }) {
    const teamId = userData?.teamId;
    const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState('');
    const [inviteError, setInviteError] = useState('');
    const [visitors, setVisitors] = useState([]);
    const [migrating, setMigrating] = useState(false);
    const [migrationDone, setMigrationDone] = useState(false);

    useEffect(() => {
        if (!teamId) return;

        // Listen to team members (users with matching teamId)
        const unsubUsers = onSnapshot(
            query(collection(db, 'users'), where('teamId', '==', teamId)),
            (snapshot) => {
                const usersList = [];
                snapshot.forEach((d) => {
                    usersList.push({ id: d.id, ...d.data() });
                });
                setUsers(usersList);
                setLoading(false);
            },
            (err) => {
                console.error("Error listening to users:", err);
                setLoading(false);
            }
        );

        // Listen to team invitations
        const unsubInvitations = onSnapshot(
            query(collection(db, 'teams', teamId, 'invitations')),
            (snapshot) => {
                const invList = [];
                snapshot.forEach((d) => {
                    invList.push({ id: d.id, ...d.data() });
                });
                setInvitations(invList);
            },
            (err) => {
                console.error("Error listening to invitations:", err);
            }
        );

        // Visitors listener
        const unsubVisitors = onSnapshot(
            query(collection(db, 'visitors'), orderBy('visitedAt', 'desc'), limit(50)),
            (snapshot) => {
                const list = [];
                snapshot.forEach((d) => list.push({ id: d.id, ...d.data() }));
                setVisitors(list);
            }
        );

        return () => {
            unsubUsers();
            unsubInvitations();
            unsubVisitors();
        };
    }, [teamId]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { teamRole: newRole, role: newRole === 'owner' || newRole === 'admin' ? 'admin' : 'user' });
        } catch (err) {
            console.error("Error updating role:", err);
            alert("Эрх өөрчлөхөд алдаа гарлаа.");
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        const email = inviteEmail.trim().toLowerCase();

        if (!email) return;
        if (!email.includes('@')) {
            setInviteError('Зөв имэйл хаяг оруулна уу');
            return;
        }

        setInviting(true);
        setInviteError('');
        setInviteSuccess('');

        try {
            // Check if already invited
            const existingInvite = query(collection(db, 'teams', teamId, 'invitations'), where('email', '==', email));
            const existingSnap = await getDocs(existingInvite);
            if (!existingSnap.empty) {
                setInviteError('Энэ имэйл аль хэдийн урьсан байна');
                setInviting(false);
                return;
            }

            // Check if already a team member
            const usersQuery = query(collection(db, 'users'), where('email', '==', email), where('teamId', '==', teamId));
            const usersSnap = await getDocs(usersQuery);
            if (!usersSnap.empty) {
                setInviteError('Энэ имэйл аль хэдийн багт бүртгэгдсэн байна');
                setInviting(false);
                return;
            }

            await addDoc(collection(db, 'teams', teamId, 'invitations'), {
                email: email,
                invitedBy: user.uid,
                invitedByName: user.displayName,
                invitedAt: serverTimestamp(),
                status: 'pending'
            });

            setInviteEmail('');
            setInviteSuccess(`${email} амжилттай урьлаа!`);
            setTimeout(() => setInviteSuccess(''), 4000);
        } catch (err) {
            console.error("Error inviting:", err);
            setInviteError('Алдаа гарлаа. Дахин оролдоно уу.');
        } finally {
            setInviting(false);
        }
    };

    const handleDeleteInvite = async (invId) => {
        if (window.confirm('Энэ урилгыг устгах уу?')) {
            try {
                await deleteDoc(doc(db, 'teams', teamId, 'invitations', invId));
            } catch (err) {
                console.error("Error deleting invitation:", err);
            }
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (userId === user.uid) {
            alert('Өөрийгөө устгах боломжгүй!');
            return;
        }
        if (window.confirm(`"${userName || 'Нэрсгүй'}" хэрэглэгчийг устгах уу?`)) {
            try {
                // Remove teamId from user (they'll be redirected to setup-team)
                await updateDoc(doc(db, 'users', userId), {
                    teamId: null,
                    teamRole: null,
                });
            } catch (err) {
                console.error("Error removing user:", err);
                alert('Хэрэглэгч устгахад алдаа гарлаа.');
            }
        }
    };

    const handleRejectUser = async (userId, userName) => {
        if (window.confirm(`"${userName || 'Нэрсгүй'}" хэрэглэгчийн хүсэлтийг татгалзах уу?`)) {
            try {
                await updateDoc(doc(db, 'users', userId), {
                    teamId: null,
                    teamRole: null,
                });
            } catch (err) {
                console.error("Error rejecting user:", err);
                alert('Алдаа гарлаа.');
            }
        }
    };

    const handleRunMigration = async () => {
        if (!window.confirm('Хуучин data-г шинэ team бүтэц рүү шилжүүлэх үү? Зөвхөн нэг удаа ажиллуулна!')) return;
        setMigrating(true);
        try {
            const result = await runMigration(user.uid, teamId);
            if (result.success) {
                setMigrationDone(true);
                alert(`Migration амжилттай! ${JSON.stringify(result.results)}`);
                window.location.reload();
            } else {
                alert('Migration алдаа: ' + result.error);
            }
        } catch (err) {
            console.error('Migration error:', err);
            alert('Migration алдаа гарлаа');
        } finally {
            setMigrating(false);
        }
    };

    const pendingUsers = users.filter(u => u.teamRole === 'pending' || u.role === 'pending');
    const activeUsers = users.filter(u => u.teamRole !== 'pending' && u.role !== 'pending');
    const pendingInvitations = invitations.filter(i => i.status === 'pending');
    const acceptedInvitations = invitations.filter(i => i.status === 'accepted');

    const getRoleBadge = (role) => {
        switch (role) {
            case 'owner':
                return <span className="badge badge-admin"><span className="badge-dot"></span> Эзэмшигч</span>;
            case 'admin':
                return <span className="badge badge-admin"><span className="badge-dot"></span> Админ</span>;
            case 'member':
                return <span className="badge badge-user"><span className="badge-dot"></span> Гишүүн</span>;
            default:
                return <span className="badge badge-pending"><span className="badge-dot"></span> {role || 'Хүлээгдэж буй'}</span>;
        }
    };

    return (
        <div className="page-container mobile-page">
            <div className="mobile-page-header">
                <h1 className="mobile-page-title">⚙️ Админ</h1>
                <Link to="/" className="cal-back-btn-header">← Буцах</Link>
            </div>

            {loading ? (
                <div className="loading-spinner-inline">
                    <span className="spinner"></span>
                    <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Ачаалж байна...</p>
                </div>
            ) : (
                <div className="content">
                    {/* Migration button — for admins who haven't migrated yet */}
                    {!migrationDone && (
                        <div className="admin-section">
                            <div className="glass-card" style={{ textAlign: 'center' }}>
                                <h3>🔄 Data шилжүүлэлт</h3>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                                    Хуучин data-г шинэ team бүтэц рүү шилжүүлнэ (нэг удаа)
                                </p>
                                <button className="btn btn-primary" onClick={handleRunMigration} disabled={migrating}>
                                    {migrating ? <><span className="spinner"></span> Шилжүүлж байна...</> : '🔄 Migration ажиллуулах'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Invite Section */}
                    <div className="admin-section">
                        <div className="glass-card">
                            <div className="section-header">
                                <h3>✉️ Gmail-ээр урих</h3>
                            </div>
                            <form className="invite-form" onSubmit={handleInvite}>
                                <input type="email" value={inviteEmail}
                                    onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); }}
                                    placeholder="example@gmail.com" required />
                                <button type="submit" className="btn btn-primary" disabled={inviting}>
                                    {inviting ? <span className="spinner"></span> : '📨 Урих'}
                                </button>
                            </form>
                            {inviteSuccess && <div className="invite-success">✅ {inviteSuccess}</div>}
                            {inviteError && <div className="error-message" style={{ marginTop: '0.5rem', marginBottom: 0 }}>{inviteError}</div>}

                            {pendingInvitations.length > 0 && (
                                <div className="mt-3">
                                    <div className="section-header">
                                        <h3 style={{ fontSize: '0.85rem' }}>Урьсан хүмүүс</h3>
                                        <span className="count">{pendingInvitations.length}</span>
                                    </div>
                                    <div className="invited-list">
                                        {pendingInvitations.map(inv => (
                                            <div key={inv.id} className="invited-card">
                                                <span className="invited-email">📧 {inv.email}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span className="invited-status pending">Хүлээгдэж буй</span>
                                                    <button className="btn-icon" onClick={() => handleDeleteInvite(inv.id)} title="Устгах">✕</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {acceptedInvitations.length > 0 && (
                                <div className="mt-3">
                                    <div className="section-header">
                                        <h3 style={{ fontSize: '0.85rem' }}>Нэгдсэн</h3>
                                        <span className="count">{acceptedInvitations.length}</span>
                                    </div>
                                    <div className="invited-list">
                                        {acceptedInvitations.map(inv => (
                                            <div key={inv.id} className="invited-card">
                                                <span className="invited-email">📧 {inv.email}</span>
                                                <span className="invited-status accepted">✓ Нэгдсэн</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Users */}
                    <div className="admin-section">
                        <div className="section-header">
                            <h3>👥 Багийн гишүүд</h3>
                            <span className="count">{activeUsers.length}</span>
                        </div>
                        <div className="user-list">
                            {activeUsers.map(u => (
                                <div key={u.id} className="user-card">
                                    <div className="user-info">
                                        {u.photoURL ? (
                                            <img src={u.photoURL} alt={u.name} className="user-avatar" />
                                        ) : (
                                            <div className="user-avatar-placeholder">
                                                {(u.name || u.email || '?').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="user-details">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span className="user-name">{u.name || 'Нэрсгүй'}</span>
                                                {getRoleBadge(u.teamRole)}
                                            </div>
                                            <div className="user-email">{u.email}</div>
                                        </div>
                                    </div>
                                    <div className="user-actions">
                                        {u.teamRole !== 'owner' && u.teamRole !== 'admin' && (
                                            <button className="btn btn-sm btn-secondary" onClick={() => handleRoleChange(u.id, 'admin')}>⚡ Админ</button>
                                        )}
                                        {u.teamRole === 'admin' && userData?.teamRole === 'owner' && (
                                            <button className="btn btn-sm btn-secondary" onClick={() => handleRoleChange(u.id, 'member')}>Гишүүн</button>
                                        )}
                                        {u.id !== user.uid && u.teamRole !== 'owner' && (
                                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(u.id, u.name)}>🗑 Хасах</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Visitor Log */}
            <div className="admin-card glass">
                <h3>👁️ Зочдын бүртгэл <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>({visitors.length})</span></h3>
                <div className="visitors-list">
                    {visitors.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Зочин алга</p>
                    ) : (
                        visitors.map(v => (
                            <div key={v.id} className="visitor-item">
                                <div className="visitor-info">
                                    <span className="visitor-uid">{v.name || v.uid?.slice(0, 8)}</span>
                                    <span className="visitor-meta">
                                        {v.browser} · {v.os} · {v.deviceType}
                                    </span>
                                    {v.city && <span className="visitor-meta">📍 {v.city}, {v.country}</span>}
                                </div>
                                <span className="visitor-time">
                                    {v.visitedAt?.toDate ? v.visitedAt.toDate().toLocaleString('mn-MN') : ''}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default Admin;
